// apps/client/src/characters/villagers.ts (M2-T11) — villager routine helpers on the client (spec §9.13, rev 5 item 13): the per-town
// schedule world, the pose cache every client shares with the server, and the cream tags over villager houses (rev 9 item 3).
import { tuning } from '@whiskerton/content';
import type { HouseState, TownInfo, VillagerPause, VillagerPublic, WorldObject } from '@whiskerton/shared';
import { generateTown } from '@whiskerton/shared/map/generate';
import { VillagerPoseCache, scheduleWorld, type ScheduleWorld, type VillagerPose } from '@whiskerton/shared/villagers/schedule';
import type { HouseTag } from './nametags';

export interface ClientVillagerWorld extends ScheduleWorld { seed: number; timeZone: string }
/** D-40: built once per town from generateTown(seed, { genVersion }), never from the live object list. */
export function createVillagerWorld(town: Pick<TownInfo, 'seed' | 'genVersion' | 'timeZone'>): ClientVillagerWorld {
  return { ...scheduleWorld(generateTown(town.seed, { genVersion: town.genVersion })), seed: town.seed, timeZone: town.timeZone };
}

/** Rev 9 item 3 (DESIGN.md "Chips / Name tags"): every house whose HouseState.occupant is a known villager gets a cream tag. */
export function villagerHouseTags(objects: readonly WorldObject[], villagers: readonly VillagerPublic[]): HouseTag[] {
  const byId = new Map(villagers.map((v) => [v.id, v]));
  const out: HouseTag[] = [];
  for (const o of objects) {
    if (o.kind !== 'house') continue;
    const occ = (o.state as HouseState).occupant;
    const v = occ?.kind === 'villager' ? byId.get(occ.id) : undefined;
    if (v) out.push({ id: o.id, name: v.name, color: 'villager', at: { x: o.x + tuning.town.houseW / 2, y: o.y + tuning.town.houseH / 2 } });
  }
  return out;
}

/** Rev 5 item 13: poses come from the shared VillagerPoseCache (the server's path too), so clients and server cannot drift.
 *  The unpaused schedule (the A* work) is cached per villager per day; a villager.paused/resumed event only re-runs applyPauses. */
export class RoutineCache {
  private readonly poses: VillagerPoseCache;
  constructor(world: ClientVillagerWorld) { this.poses = new VillagerPoseCache(world, world.seed, world.timeZone); }
  poseFor(v: VillagerPublic, pauses: readonly VillagerPause[], nowMs: number): VillagerPose & { homeAwake: boolean } {
    return this.poses.poseAt(v, pauses, nowMs);
  }
}
